import React, { useState, useRef, useEffect } from 'react'
import {
  Bot,
  Send,
  X,
  RotateCcw,
  Sparkles,
  AlertCircle,
  Loader2,
  Database,
  Paperclip,
  ClipboardType,
  FileSpreadsheet,
} from 'lucide-react'
import {
  sendChatMessage,
  type ChatHistoryItem,
  extractQuoteFromFile,
  extractQuoteFromText,
  type ExtractQuoteResultData,
  type FileMetadataDto,
} from './api'
import { MarkdownRenderer } from './MarkdownRenderer'
import { QuotePreviewModal } from './QuotePreviewModal'
import { MaterialProposalModal } from './MaterialProposalModal'

interface MessageItem {
  id: string
  role: 'user' | 'assistant'
  content: string
  toolsCalled?: string[]
}

const SAMPLE_QUESTIONS = [
  'Vật tư nào đang dưới ngưỡng an toàn hoặc hết hàng?',
  'Tìm mã vật tư ván MDF hoặc MFC',
  'Xem trạng thái các đơn hàng bán gần đây',
  'Tra cứu định mức BOM sản phẩm',
]

const ImageThumbnailPreview: React.FC<{ file: File; onRemove: () => void }> = ({ file, onRemove }) => {
  const [url, setUrl] = useState<string>('')

  useEffect(() => {
    let objectUrl = ''
    try {
      objectUrl = URL.createObjectURL(file)
      setUrl(objectUrl)
    } catch {
      // In tests or mock environments createObjectURL might not be available
      setUrl('')
    }
    return () => {
      if (objectUrl) {
        try {
          URL.revokeObjectURL(objectUrl)
        } catch {
          // Ignore
        }
      }
    }
  }, [file])

  return (
    <div className="relative group flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white p-1 shadow-2xs pr-2">
      {url ? (
        <img
          src={url}
          alt={file.name}
          className="h-10 w-10 rounded-md object-cover border border-slate-200"
        />
      ) : (
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-100 text-[10px] text-slate-500 font-mono">
          IMG
        </div>
      )}
      <div className="flex flex-col text-[11px] max-w-[130px]">
        <span className="truncate font-medium text-slate-700" title={file.name}>
          {file.name}
        </span>
        <span className="text-[10px] text-slate-400">
          {(file.size / 1024).toFixed(0)} KB
        </span>
      </div>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Xóa ảnh ${file.name}`}
        className="rounded-full p-1 text-slate-400 hover:bg-rose-100 hover:text-rose-600 transition-colors ml-1 cursor-pointer"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

export const AiAssistantWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<MessageItem[]>([])
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Giai đoạn 2 & 3: Bảng xem trước báo giá & đề xuất vật tư
  const [isExtracting, setIsExtracting] = useState(false)
  const [extractProgressMessage, setExtractProgressMessage] = useState<string | null>(null)
  const [previewModalOpen, setPreviewModalOpen] = useState(false)
  const [previewData, setPreviewData] = useState<ExtractQuoteResultData | null>(null)
  const [previewFileMetadata, setPreviewFileMetadata] = useState<FileMetadataDto | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [textPasteModalOpen, setTextPasteModalOpen] = useState(false)
  const [textPasteInput, setTextPasteInput] = useState('')
  const [imageAttachments, setImageAttachments] = useState<File[]>([])
  const [materialProposal, setMaterialProposal] = useState<{ id: string; title: string } | null>(null)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Scroll to bottom when messages update
  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, isLoading, isOpen, isExtracting])

  // Focus textarea when panel opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 150)
    }
  }, [isOpen])

  const addImages = (files: File[]) => {
    setErrorMessage(null)
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp']
    const rejectedType: string[] = []
    const rejectedSize: string[] = []
    const accepted: File[] = []

    for (const file of files) {
      if (!validTypes.includes(file.type.toLowerCase())) {
        rejectedType.push(file.name)
        continue
      }
      if (file.size > 10 * 1024 * 1024) {
        rejectedSize.push(file.name)
        continue
      }
      accepted.push(file)
    }

    if (rejectedType.length > 0) {
      setErrorMessage(`Chỉ nhận ảnh PNG, JPEG hoặc WebP. File bị từ chối: ${rejectedType.join(', ')}`)
    } else if (rejectedSize.length > 0) {
      setErrorMessage(`Kích thước ảnh vượt quá giới hạn 10MB: ${rejectedSize.join(', ')}`)
    }

    if (accepted.length > 0) {
      setImageAttachments((current) => {
        const total = [...current, ...accepted]
        if (total.length > 5) {
          setErrorMessage('Tối đa 5 ảnh đính kèm mỗi lượt. Các ảnh vượt quá đã được bỏ qua.')
        }
        return total.slice(0, 5)
      })
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = Array.from(e.clipboardData?.items || [])
    const imageFiles: File[] = []

    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile()
        if (file) imageFiles.push(file)
      }
    }

    if (imageFiles.length === 0 && e.clipboardData?.files?.length) {
      for (const file of Array.from(e.clipboardData.files)) {
        if (file.type.startsWith('image/')) {
          imageFiles.push(file)
        }
      }
    }

    if (imageFiles.length > 0) {
      e.preventDefault()
      addImages(imageFiles)
    }
    // If no images found, default behavior pastes text normally into textarea
  }

  const handleSend = async (textToSend?: string) => {
    const text = (textToSend ?? inputValue).trim()
    if ((!text && imageAttachments.length === 0) || isLoading || isExtracting) return

    setErrorMessage(null)
    setInputValue('')

    const currentImages = [...imageAttachments]
    setImageAttachments([])

    const userMsgContent = text || (currentImages.length > 0 ? 'Phân tích hình ảnh đính kèm' : '')
    const userMsg: MessageItem = {
      id: `usr-${Date.now()}`,
      role: 'user',
      content: userMsgContent,
    }

    const newMessages = [...messages, userMsg]
    setMessages(newMessages)
    setIsLoading(true)

    // Build history for API
    const historyPayload: ChatHistoryItem[] = messages.slice(-10).map((m) => ({
      role: m.role,
      content: m.content,
    }))

    try {
      let imagesPayload: Array<{ name: string; type: string; base64: string }> | undefined
      if (currentImages.length > 0) {
        imagesPayload = await Promise.all(
          currentImages.map(async (file) => {
            const arrayBuffer = await file.arrayBuffer()
            const bytes = new Uint8Array(arrayBuffer)
            let binary = ''
            for (let i = 0; i < bytes.byteLength; i++) {
              binary += String.fromCharCode(bytes[i])
            }
            const base64 = btoa(binary)
            return {
              name: file.name,
              type: file.type,
              base64,
            }
          }),
        )
      }

      const res = await sendChatMessage(userMsgContent, historyPayload, imagesPayload)

      if (!res.ok) {
        setErrorMessage(res.error?.message || 'Có lỗi xảy ra khi gọi trợ lý AI.')
      } else if (res.data) {
        const assistantMsg: MessageItem = {
          id: `ast-${Date.now()}`,
          role: 'assistant',
          content: res.data.message,
          toolsCalled: res.data.toolsCalled,
        }
        setMessages((prev) => [...prev, assistantMsg])

        if (res.data.proposal) {
          if (res.data.proposal.type === 'CREATE_MATERIALS') {
            setMaterialProposal({ id: res.data.proposal.id, title: res.data.proposal.title })
          } else if (res.data.proposal.type === 'QUOTE_INTAKE') {
            if (res.data.proposal.data) {
              setPreviewData(res.data.proposal.data)
              setPreviewModalOpen(true)
            }
          }
        }
      }
    } catch (err) {
      setErrorMessage(
        err instanceof Error
          ? err.message
          : 'Không thể kết nối tới máy chủ ERP. Vui lòng thử lại.',
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleNewConversation = () => {
    setMessages([])
    setErrorMessage(null)
    setInputValue('')
    setImageAttachments([])
    inputRef.current?.focus()
  }

  // Handle uploaded file
  const handleProcessFile = async (file: File) => {
    setErrorMessage(null)

    // If file is an image, add to image queue
    if (file.type.startsWith('image/')) {
      addImages([file])
      return
    }

    setIsExtracting(true)
    setExtractProgressMessage(`Đang đọc file ${file.name} và trích xuất dữ liệu...`)

    try {
      const res = await extractQuoteFromFile(file)
      if (!res.ok) {
        setErrorMessage(res.error?.message || 'Không thể trích xuất dữ liệu từ file.')
      } else if (res.data) {
        setPreviewData(res.data)
        setPreviewFileMetadata(res.file_metadata || null)
        setPreviewModalOpen(true)
      }
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Lỗi khi xử lý file báo giá.')
    } finally {
      setIsExtracting(false)
      setExtractProgressMessage(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  // Handle pasted text
  const handleProcessPastedText = async () => {
    const text = textPasteInput.trim()
    if (!text) return

    setErrorMessage(null)
    setTextPasteModalOpen(false)
    setTextPasteInput('')
    setIsExtracting(true)
    setExtractProgressMessage('Đang phân tích và chuẩn hóa văn bản báo giá qua AI...')

    try {
      const res = await extractQuoteFromText(text)
      if (!res.ok) {
        setErrorMessage(res.error?.message || 'Không thể trích xuất dữ liệu từ văn bản.')
      } else if (res.data) {
        setPreviewData(res.data)
        setPreviewFileMetadata(null)
        setPreviewModalOpen(true)
      }
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Lỗi khi phân tích văn bản.')
    } finally {
      setIsExtracting(false)
      setExtractProgressMessage(null)
    }
  }

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const files = Array.from(e.dataTransfer.files || [])
    if (files.length === 0) return

    const images = files.filter((f) => f.type.startsWith('image/'))
    const nonImages = files.filter((f) => !f.type.startsWith('image/'))

    if (images.length > 0) {
      addImages(images)
    }
    if (nonImages.length > 0) {
      handleProcessFile(nonImages[0])
    }
  }

  const handleQuoteSuccess = (quoteResult: { quote_id?: string; title: string; count: number }) => {
    setPreviewModalOpen(false)
    const assistantMsg: MessageItem = {
      id: `ast-quote-${Date.now()}`,
      role: 'assistant',
      content: `✓ **Đã nhập thành công báo giá vào ERP!**\n\n- **Tiêu đề:** ${quoteResult.title}\n- **Mã báo giá:** \`${quoteResult.quote_id || 'MỚI'}\`\n- **Số lượng dòng giá:** ${quoteResult.count} dòng\n- **Trạng thái:** Báo giá đã được tạo (DRAFT). Các dòng chưa chọn vật tư ở trạng thái **NEEDS_MAPPING** theo đúng quy định hệ thống.`,
    }
    setMessages((prev) => [...prev, assistantMsg])
  }

  return (
    <>
      {/* Floating Action Button (bottom-right) */}
      {!isOpen && (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full bg-[var(--color-primary)] px-4 py-2.5 text-sm font-medium text-white shadow-lg hover:bg-[var(--color-primary-hover)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:ring-offset-2 transition-all active:scale-95 print:hidden cursor-pointer"
          aria-label="Mở Trợ lý AI"
        >
          <Bot className="h-4 w-4" />
          <span>Trợ lý AI</span>
          <span className="flex h-2 w-2 rounded-full bg-emerald-400" />
        </button>
      )}

      {/* Chat Slide-Over Panel */}
      {isOpen && (
        <aside
          role="dialog"
          aria-label="Panel Trợ lý AI"
          aria-modal="true"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`fixed inset-y-0 right-0 z-50 flex w-[680px] max-w-[95vw] flex-col bg-white shadow-2xl border-l border-slate-200 print:hidden animate-in slide-in-from-right duration-200 ${
            isDragging ? 'ring-2 ring-inset ring-[var(--color-primary)] bg-sky-50/50' : ''
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-3">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--color-primary)] text-white shadow-xs">
                <Bot className="h-4 w-4" />
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  <h2 className="text-sm font-semibold text-slate-800">
                    Trợ lý AI ERP
                  </h2>
                  <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium text-sky-800">
                    Giai đoạn 3
                  </span>
                </div>
                <p className="text-[11px] text-slate-500">
                  Tra cứu dữ liệu, Tạo vật tư & Nhập báo giá thông minh
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={handleNewConversation}
                title="Cuộc trò chuyện mới"
                aria-label="Cuộc trò chuyện mới"
                className="rounded p-1.5 text-slate-500 hover:bg-slate-200 hover:text-slate-800 transition-colors cursor-pointer"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                title="Đóng panel"
                aria-label="Đóng panel"
                className="rounded p-1.5 text-slate-500 hover:bg-slate-200 hover:text-slate-800 transition-colors cursor-pointer"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Messages Body */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3.5 bg-slate-50/50">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-6 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--color-primary-soft)] text-[var(--color-primary)] mb-3">
                  <Sparkles className="h-6 w-6" />
                </div>
                <h3 className="text-sm font-semibold text-slate-800 mb-1">
                  Chào bạn! Tôi có thể hỗ trợ gì?
                </h3>
                <p className="text-xs text-slate-500 max-w-xs mb-4">
                  Tra cứu dữ liệu tồn kho, BOM hoặc dán ảnh/kéo thả file để tạo vật tư hoặc nhập báo giá.
                </p>

                {/* File Upload Drop Banner */}
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full mb-4 cursor-pointer rounded-xl border-2 border-dashed border-slate-300 bg-white p-3.5 text-center hover:border-[var(--color-primary)] hover:bg-[var(--color-primary-soft)]/20 transition-all shadow-2xs group"
                >
                  <FileSpreadsheet className="h-6 w-6 text-slate-400 mx-auto mb-1.5 group-hover:text-[var(--color-primary)] transition-colors" />
                  <p className="text-xs font-medium text-slate-700">
                    Kéo thả file hoặc ảnh báo giá vào đây
                  </p>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    Hỗ trợ Excel (.xlsx), PDF, ảnh (.png, .jpg, .webp) tối đa 10MB
                  </p>
                </div>

                <div className="w-full space-y-2 text-left">
                  <div className="text-[11px] font-medium uppercase tracking-wider text-slate-400 px-1">
                    Gợi ý câu hỏi nhanh:
                  </div>
                  {SAMPLE_QUESTIONS.map((q, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => handleSend(q)}
                      className="w-full rounded-lg border border-slate-200 bg-white p-2.5 text-xs text-slate-700 hover:border-[var(--color-primary)] hover:bg-[var(--color-primary-soft)]/30 hover:text-[var(--color-primary)] transition-colors text-left shadow-2xs cursor-pointer"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((m) => (
                <div
                  key={m.id}
                  className={`flex flex-col ${m.role === 'user' ? 'items-end' : 'items-start w-full min-w-0'}`}
                >
                  <div
                    className={`rounded-2xl px-3.5 py-2.5 text-xs sm:text-sm shadow-2xs ${
                      m.role === 'user'
                        ? 'bg-[var(--color-primary)] text-white rounded-tr-xs max-w-[85%]'
                        : 'bg-white text-slate-800 border border-slate-200/90 rounded-tl-xs w-full max-w-full min-w-0'
                    }`}
                  >
                    {m.role === 'user' ? (
                      <p className="whitespace-pre-wrap leading-relaxed break-words break-all">{m.content}</p>
                    ) : (
                      <div className="w-full max-w-full min-w-0 overflow-hidden">
                        <MarkdownRenderer content={m.content} />
                        {m.toolsCalled && m.toolsCalled.length > 0 && (
                          <div className="mt-2.5 pt-2 border-t border-slate-100 flex items-center gap-1.5 text-[10.5px] text-slate-400">
                            <Database className="h-3 w-3" />
                            <span>Đã truy vấn dữ liệu ERP: {m.toolsCalled.join(', ')}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}

            {/* Extraction Loading Indicator */}
            {isExtracting && (
              <div className="flex items-start gap-2">
                <div className="flex items-center gap-2 rounded-2xl rounded-tl-xs bg-white border border-slate-200 px-3.5 py-2.5 text-xs text-slate-700 shadow-2xs">
                  <Loader2 className="h-4 w-4 animate-spin text-[var(--color-primary)] shrink-0" />
                  <span className="font-medium text-slate-700">
                    {extractProgressMessage || 'Đang xử lý báo giá...'}
                  </span>
                </div>
              </div>
            )}

            {/* Chat Loading Indicator */}
            {isLoading && !isExtracting && (
              <div className="flex items-start gap-2">
                <div className="flex items-center gap-2 rounded-2xl rounded-tl-xs bg-white border border-slate-200 px-3.5 py-2.5 text-xs text-slate-600 shadow-2xs">
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-[var(--color-primary)]" />
                  <span>Trợ lý đang truy vấn dữ liệu...</span>
                </div>
              </div>
            )}

            {/* Error Message Banner */}
            {errorMessage && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800"
              >
                <AlertCircle className="h-4 w-4 shrink-0 text-amber-600 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium">Thông báo</p>
                  <p className="mt-0.5 text-amber-700 leading-relaxed">{errorMessage}</p>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Footer Input Box */}
          <div className="border-t border-slate-200 bg-white p-3">
            <div className="relative rounded-lg border border-slate-300 bg-white focus-within:border-[var(--color-primary)] focus-within:ring-1 focus-within:ring-[var(--color-primary)] transition-all">
              <textarea
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onPaste={handlePaste}
                onKeyDown={handleKeyDown}
                placeholder="Nhập câu hỏi, câu lệnh hoặc dán ảnh (Ctrl+V) vào đây..."
                rows={2}
                disabled={isLoading || isExtracting}
                className="w-full resize-none border-0 bg-transparent p-2.5 text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none"
              />

              {/* Image Attachments Thumbnail Strip */}
              {imageAttachments.length > 0 && (
                <div
                  className="flex flex-wrap gap-2 px-2.5 pb-2.5 pt-1 border-t border-slate-100 bg-slate-50/70"
                  aria-label="Ảnh đính kèm"
                >
                  {imageAttachments.map((file, index) => (
                    <ImageThumbnailPreview
                      key={`${file.name}-${index}`}
                      file={file}
                      onRemove={() =>
                        setImageAttachments((items) => items.filter((_, i) => i !== index))
                      }
                    />
                  ))}
                </div>
              )}

              {/* Action Toolbar */}
              <div className="flex items-center justify-between border-t border-slate-100 px-2 py-1.5 bg-slate-50/50 rounded-b-lg">
                <div className="flex items-center gap-1">
                  {/* File Upload Button */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.pdf,.png,.jpg,.jpeg,.webp"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0]
                      if (f) handleProcessFile(f)
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isLoading || isExtracting}
                    title="Đính kèm file hoặc ảnh (Excel, PDF, PNG, JPG, WebP)"
                    aria-label="Đính kèm file báo giá"
                    className="flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-slate-200 hover:text-slate-800 transition-colors disabled:opacity-40 cursor-pointer"
                  >
                    <Paperclip className="h-4 w-4" />
                  </button>

                  {/* Paste text button */}
                  <button
                    type="button"
                    onClick={() => setTextPasteModalOpen(true)}
                    disabled={isLoading || isExtracting}
                    title="Dán văn bản báo giá"
                    aria-label="Dán văn bản báo giá"
                    className="flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-slate-200 hover:text-slate-800 transition-colors disabled:opacity-40 cursor-pointer"
                  >
                    <ClipboardType className="h-4 w-4" />
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-slate-400">
                    Giai đoạn 3 • Tra cứu & Đề xuất
                  </span>
                  <button
                    type="button"
                    onClick={() => handleSend()}
                    disabled={(!inputValue.trim() && imageAttachments.length === 0) || isLoading || isExtracting}
                    aria-label="Gửi tin nhắn"
                    className="flex h-7 w-7 items-center justify-center rounded-md bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)] disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer"
                  >
                    <Send className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </aside>
      )}

      {/* Paste Text Modal */}
      {textPasteModalOpen && (
        <div
          role="dialog"
          aria-label="Dán văn bản báo giá"
          aria-modal="true"
          className="fixed inset-0 z-60 flex items-center justify-center bg-black/50 p-4 print:hidden"
        >
          <div className="w-full max-w-lg rounded-xl bg-white p-5 shadow-2xl border border-slate-200 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-800">
                Dán văn bản báo giá nhà cung cấp
              </h3>
              <button
                type="button"
                onClick={() => setTextPasteModalOpen(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">
              Dán nội dung tin nhắn, bảng giá hoặc email báo giá của nhà cung cấp vào khung bên dưới. Trợ lý AI sẽ trích xuất và chuẩn hóa thành bảng giá.
            </p>
            <textarea
              rows={8}
              value={textPasteInput}
              onChange={(e) => setTextPasteInput(e.target.value)}
              placeholder="Ví dụ: Ván ép thông 9mm giá 273.000đ/tấm, Ván ép sofa 12mm giá 214.330đ..."
              className="w-full rounded-lg border border-slate-300 p-3 text-xs focus:border-[var(--color-primary)] focus:outline-none"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setTextPasteModalOpen(false)}
                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={handleProcessPastedText}
                disabled={!textPasteInput.trim()}
                className="rounded-lg bg-[var(--color-primary)] px-4 py-1.5 text-xs font-medium text-white hover:bg-[var(--color-primary-hover)] disabled:opacity-40"
              >
                Phân tích bằng AI
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quote Preview & Confirmation Modal */}
      {previewModalOpen && previewData && (
        <QuotePreviewModal
          isOpen={previewModalOpen}
          data={previewData}
          fileMetadata={previewFileMetadata}
          onClose={() => setPreviewModalOpen(false)}
          onSuccess={handleQuoteSuccess}
        />
      )}

      {/* Material Proposal Modal */}
      {materialProposal && (
        <MaterialProposalModal
          proposalId={materialProposal.id}
          title={materialProposal.title}
          onClose={() => setMaterialProposal(null)}
          onConfirmed={(items) => {
            setMaterialProposal(null)
            const listText = items.map((item) => `- \`${item.code}\` ${item.name}`).join('\n')
            setMessages((prev) => [
              ...prev,
              {
                id: `ast-material-confirmed-${Date.now()}`,
                role: 'assistant',
                content: `✓ **Đã tạo thành công ${items.length} vật tư vào hệ thống ERP!**\n\n${listText}`,
              },
            ])
          }}
        />
      )}
    </>
  )
}
